import { Message, Model } from '@shared/types';
import {
  ArrowUpRight,
  Box,
  ChevronLeft,
  ChevronRight,
  History,
  ChevronDown,
  Loader2,
  Sparkles,
  RotateCcw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

import { cn, PARAMETRIC_MODELS } from '@/lib/utils';
import { useConversation } from '@/services/conversationService';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCurrentMessage } from '@/contexts/CurrentMessageContext';
import { useCallback, useMemo, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TreeNode } from '@shared/Tree';
import {
  useRestoreMessageMutation,
  useRetryMessageMutation,
  useIsLoading,
} from '@/services/messageService';

interface AssistantMessageProps {
  message: TreeNode<Message>;
  currentVersion: number;
}

export function AssistantMessage({
  message,
  currentVersion,
}: AssistantMessageProps) {
  const { conversation, updateConversation } = useConversation();
  const { currentMessage, setCurrentMessage } = useCurrentMessage();
  const { mutate: restoreMessage } = useRestoreMessageMutation();
  const { mutate: retryMessage } = useRetryMessageMutation();
  const isLoading = useIsLoading();
  const model = message.content.model ?? 'anthropic/claude-sonnet-4.5';

  const changeLeaf = useCallback(
    (messageId: string) => {
      updateConversation?.({
        ...conversation,
        current_message_leaf_id: messageId,
      });
    },
    [updateConversation, conversation],
  );

  const branchIndex = useMemo(
    () => message.siblings.findIndex((branch) => branch.id === message.id),
    [message.siblings, message.id],
  );

  const leafNodes = useMemo(
    () =>
      message.siblings.map((branch) => {
        let current = branch;
        while (current.children && current.children.length > 0) {
          current = current.children[0];
        }
        return current;
      }),
    [message.siblings],
  );

  const isLastMessage = conversation.current_message_leaf_id === message.id;

  return (
    <div className="group flex gap-2">
      {/* Avatar */}
      <div className="flex-shrink-0">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-dc-accent-primary/20 to-dc-accent-secondary/20 ring-1 ring-dc-accent-primary/30">
          <img
            src={`${import.meta.env.BASE_URL}/daddycad-logo.png`}
            alt="DaddyCad"
            className="h-4 w-4"
          />
        </div>
      </div>

      {/* Message Content */}
      <div className="min-w-0 flex-1">
        {/* Header */}
        <div className="mb-1 flex items-center gap-1.5">
          <span className="text-xs font-semibold text-dc-text-primary">DaddyCad</span>
          <span className="flex items-center gap-0.5 rounded-full bg-dc-accent-primary/10 px-1.5 py-0.5 text-[10px] text-dc-accent-primary">
            <Sparkles className="h-2.5 w-2.5" />
            AI
          </span>
        </div>

        {/* Message Body */}
        <div className="rounded-xl rounded-tl-sm border border-dc-border-primary bg-dc-bg-tertiary/50 p-3 backdrop-blur-sm transition-all duration-200 hover:border-dc-border-hover">
          {message.content.error ? (
            <div className="flex items-center gap-2 text-red-400">
              <span className="text-xs">We ran into some trouble with your prompt</span>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {/* Text content */}
              {message.content.text && (
                <p className="text-xs leading-relaxed text-dc-text-primary">
                  {message.content.text}
                </p>
              )}

              {/* Tool calls / Building status */}
              {message.content.toolCalls && message.content.toolCalls.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  {message.content.toolCalls.map((toolCall) => (
                    <div
                      key={toolCall.id ?? `${toolCall.name}`}
                      className="flex items-center gap-2 rounded-lg bg-dc-bg-elevated/50 px-3 py-2 ring-1 ring-dc-border-primary"
                    >
                      <div className="flex h-6 w-6 items-center justify-center rounded-md bg-gradient-to-br from-dc-accent-primary/20 to-dc-accent-secondary/20">
                        <Box className="h-3 w-3 text-dc-accent-primary" />
                      </div>
                      <div className="flex-1">
                        {toolCall.status === 'pending' && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-medium text-dc-text-primary">Building your design...</span>
                            <Loader2 className="h-3 w-3 animate-spin text-dc-accent-primary" />
                          </div>
                        )}
                        {toolCall.status === 'error' && (
                          <span className="text-xs text-red-400">Failed to generate</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Object/Artifact Button */}
              {message.content.artifact && (
                <ObjectButton
                  message={message}
                  currentMessage={currentMessage}
                  setCurrentMessage={setCurrentMessage}
                  currentVersion={currentVersion}
                />
              )}
            </div>
          )}
        </div>

        {/* Action Bar */}
        {(message.siblings.length > 1 || !isLastMessage || message.parent_message_id) && (
          <div className="mt-2 flex items-center gap-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
            {/* Restore */}
            {!isLastMessage && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => restoreMessage(message)}
                    disabled={isLoading}
                    className="h-7 gap-1.5 rounded-lg px-2 text-xs text-dc-text-tertiary hover:bg-dc-bg-hover hover:text-dc-text-primary"
                  >
                    <History className="h-3.5 w-3.5" />
                    Restore
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="bg-dc-bg-elevated border-dc-border-primary">
                  <span className="text-dc-text-primary">Restore this version</span>
                </TooltipContent>
              </Tooltip>
            )}

            {/* Retry */}
            {message.parent_message_id && (
              <div className="flex items-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        retryMessage({
                          model,
                          id: message.parent_message_id!,
                        });
                      }}
                      disabled={isLoading}
                      className="h-7 gap-1.5 rounded-lg rounded-r-none border-r border-dc-border-primary px-2 text-xs text-dc-text-tertiary hover:bg-dc-bg-hover hover:text-dc-text-primary"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Retry
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-dc-bg-elevated border-dc-border-primary">
                    <span className="text-dc-text-primary">Regenerate response</span>
                  </TooltipContent>
                </Tooltip>
                {model && (
                  <RetryModelSelector
                    message={message}
                    onRetry={(model) =>
                      retryMessage({ model, id: message.parent_message_id! })
                    }
                    disabled={isLoading}
                    className="h-7 rounded-l-none"
                  />
                )}
              </div>
            )}

            {/* Branch Navigation */}
            {message.siblings.length > 1 && (
              <div className="flex items-center gap-1 rounded-lg bg-dc-bg-tertiary px-1 ring-1 ring-dc-border-primary">
                <Button
                  disabled={branchIndex === 0 || isLoading}
                  variant="ghost"
                  size="icon"
                  onClick={() => changeLeaf(leafNodes[branchIndex - 1].id)}
                  className="h-6 w-6 rounded-md p-0 text-dc-text-tertiary hover:bg-dc-bg-hover hover:text-dc-text-primary disabled:opacity-30"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="min-w-[2rem] text-center text-xs font-medium text-dc-text-secondary">
                  {branchIndex + 1}/{message.siblings.length}
                </span>
                <Button
                  disabled={branchIndex === message.siblings.length - 1 || isLoading}
                  variant="ghost"
                  size="icon"
                  onClick={() => changeLeaf(leafNodes[branchIndex + 1].id)}
                  className="h-6 w-6 rounded-md p-0 text-dc-text-tertiary hover:bg-dc-bg-hover hover:text-dc-text-primary disabled:opacity-30"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ObjectButton({
  message,
  currentMessage,
  setCurrentMessage,
  currentVersion,
}: {
  message: Message;
  currentMessage: Message | null;
  setCurrentMessage: (message: Message) => void;
  currentVersion: number;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const title = message.content.artifact?.title || 'DaddyCad Object';
  const isActive = currentMessage && currentMessage.id === message.id;

  return (
    <button
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        'group/btn relative flex w-full items-center gap-2 rounded-lg p-2 transition-all duration-200',
        'bg-gradient-to-br from-dc-accent-primary/10 via-dc-bg-elevated to-dc-accent-secondary/10',
        'ring-1 ring-dc-border-primary hover:ring-dc-accent-primary/50',
        isActive && 'ring-2 ring-dc-accent-primary shadow-[0_0_15px_rgba(0,212,255,0.2)]',
      )}
      onClick={() => setCurrentMessage(message)}
    >
      {/* Icon */}
      <div className={cn(
        'flex h-7 w-7 items-center justify-center rounded-md transition-all duration-200',
        'bg-gradient-to-br from-dc-accent-primary/20 to-dc-accent-secondary/20',
        isActive && 'from-dc-accent-primary/30 to-dc-accent-secondary/30',
      )}>
        <Box className={cn(
          'h-3.5 w-3.5 transition-colors',
          isActive ? 'text-dc-accent-primary' : 'text-dc-text-secondary',
        )} />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1 text-left">
        <p className="truncate text-xs font-medium text-dc-text-primary">{title}</p>
        <p className="text-[10px] text-dc-text-tertiary">3D Model • v{currentVersion}</p>
      </div>

      {/* Action indicator */}
      <div className={cn(
        'flex h-6 items-center gap-1 rounded-md px-2 text-[10px] font-medium transition-all duration-200',
        isHovered || isActive
          ? 'bg-dc-accent-primary/20 text-dc-accent-primary'
          : 'bg-dc-bg-hover text-dc-text-tertiary',
      )}>
        {isHovered ? (
          <>
            <span>Open</span>
            <ArrowUpRight className="h-3 w-3" />
          </>
        ) : (
          <span>v{currentVersion}</span>
        )}
      </div>
    </button>
  );
}

function RetryModelSelector({
  message,
  onRetry,
  disabled,
  className,
}: {
  message: Message;
  onRetry: (modelId: Model) => void;
  disabled?: boolean;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const models = PARAMETRIC_MODELS;
  const selectedModelConfig =
    models.find((model) => model.id === message.content.model) ?? models[0];

  const availableModels = models.filter(
    (model) => model.id !== selectedModelConfig.id,
  );

  if (availableModels.length === 0) {
    return (
      <Button
        variant="ghost"
        disabled={true}
        className={cn(
          'gap-1 px-2 text-xs text-dc-text-tertiary opacity-50',
          className,
        )}
      >
        <span>{selectedModelConfig.name}</span>
      </Button>
    );
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          disabled={disabled}
          className={cn(
            'gap-1 px-2 text-xs text-dc-text-tertiary hover:bg-dc-bg-hover hover:text-dc-text-primary',
            isOpen && 'bg-dc-bg-hover',
            className,
          )}
        >
          <span>{selectedModelConfig.name}</span>
          <ChevronDown
            className={cn(
              'h-3 w-3 transition-transform duration-200',
              isOpen && 'rotate-180',
            )}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-48 rounded-xl border border-dc-border-primary bg-dc-bg-elevated p-1 backdrop-blur-xl"
        align="start"
      >
        {availableModels.map((model) => (
          <DropdownMenuItem
            key={model.id}
            className="cursor-pointer rounded-lg px-3 py-2 text-xs text-dc-text-primary hover:bg-dc-bg-hover focus:bg-dc-bg-hover"
            onClick={() => {
              onRetry(model.id);
              setIsOpen(false);
            }}
          >
            Retry with {model.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
